/* Debit note (purchase return) — printable document.
 *
 * Renders the note the pharmacy issues to a supplier when stock goes back:
 * pharmacy + supplier blocks, the returned lots with their tax breakdown, and
 * the totals that were debited to the supplier's account.
 *
 * Uses the shared htmlToPdfBlob pipeline from printInvoice.js so the output
 * matches the sales invoice (same A4 layout, pagination and rendering path).
 */
import { saveAs } from "file-saver";
import { htmlToPdfBlob, openPrintWindow } from "../printInvoice";
import { getWarehouseInfo } from "../../../Services/warehouseContext";

/* Fallback pharmacy block — mirrors printInvoice's, used when no warehouse is
   resolved (e.g. a session predating login-based resolution). */
const DEFAULT_PHARMACY = {
  name: "CureBay Medishop",
  sub: "(A unit of CureBay Pharmacy Pvt. Ltd.)",
  address:
    "Plot No-432, Khata No-428, Po-BOMIKHAL, Unit-12, PS-SAHEED NAGAR, BHUBANESWAR – 751010, Odisha",
  dl: "KH-BBS-III-44171-R / 44172-RC-19100-RX",
  gstin: "21AAKCC7552J1ZK",
  pan: "AAKCC7552J",
};

const dash = (v) => (v == null || v === "" ? "—" : v);
const num = (v) => Number(v) || 0;
const money = (v) =>
  num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const resolvePharmacy = () => {
  const info = getWarehouseInfo();
  if (!info) return DEFAULT_PHARMACY;
  return {
    name: dash(info.pharmacyName),
    sub: DEFAULT_PHARMACY.sub,
    address: dash(info.address),
    dl: dash(info.dl),
    gstin: dash(info.gstin),
    pan: dash(info.pan),
  };
};

/* yyyy-mm-dd(Thh:mm) -> dd/mm/yyyy. */
const fmtDate = (v) => {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dash(v);
};
/* Expiry prints as MM/YYYY — a lot expires at month end, the day is noise. */
const fmtExpiry = (v) => {
  const m = String(v || "").match(/^(\d{4})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : dash(v);
};

/* Indian-system amount in words, matching the sales invoice's wording. */
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const under1000 = (n) => {
  let out = "";
  if (n >= 100) {
    out += `${ONES[Math.floor(n / 100)]} Hundred`;
    n %= 100;
    if (n) out += " ";
  }
  if (n >= 20) {
    out += TENS[Math.floor(n / 10)];
    if (n % 10) out += ` ${ONES[n % 10]}`;
  } else if (n > 0) out += ONES[n];
  return out;
};
const amountInWords = (amount) => {
  const whole = Math.floor(Math.abs(num(amount)));
  const paise = Math.round((Math.abs(num(amount)) - whole) * 100);
  if (whole === 0 && paise === 0) return "Zero Rupees Only";
  const parts = [];
  const push = (value, label) => {
    if (value > 0) parts.push(`${under1000(value)} ${label}`.trim());
  };
  push(Math.floor(whole / 10000000), "Crore");
  push(Math.floor((whole % 10000000) / 100000), "Lakh");
  push(Math.floor((whole % 100000) / 1000), "Thousand");
  push(whole % 1000, "");
  let out = parts.filter(Boolean).join(" ").trim();
  out = out ? `${out} Rupees` : "";
  if (paise > 0) out += `${out ? " and " : ""}${under1000(paise)} Paise`;
  return `${out} Only`.replace(/\s+/g, " ").trim();
};

const STYLES = `
  * { box-sizing: border-box; }
  body { margin: 0; padding: 18px 20px; font-family: Arial, Helvetica, sans-serif;
         color: #101828; font-size: 11px; background: #fff; }
  .hdr { display: flex; justify-content: space-between; gap: 16px;
         border-bottom: 2px solid #101828; padding-bottom: 10px; }
  .biz-name { font-size: 15px; font-weight: 700; }
  .biz-sub { font-size: 10px; color: #667085; margin-top: 1px; }
  .biz-line { font-size: 10px; color: #344054; margin-top: 4px; line-height: 1.45; max-width: 330px; }
  .doc-title { font-size: 17px; font-weight: 700; letter-spacing: .06em; text-align: right; }
  .doc-meta { font-size: 10px; text-align: right; margin-top: 6px; line-height: 1.6; }
  .doc-meta b { font-family: "Courier New", monospace; }
  .status { display: inline-block; border: 1px solid #101828; border-radius: 3px;
            padding: 1px 6px; font-size: 9px; font-weight: 700; letter-spacing: .06em; }
  .parties { display: flex; gap: 12px; margin-top: 12px; }
  .party { flex: 1; border: 1px solid #d0d5dd; border-radius: 4px; padding: 8px 10px; }
  .party h4 { margin: 0 0 4px; font-size: 9px; letter-spacing: .08em; text-transform: uppercase; color: #667085; }
  .party .nm { font-weight: 700; font-size: 12px; }
  .party .ln { font-size: 10px; color: #344054; margin-top: 2px; line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { background: #f2f4f7; border: 1px solid #d0d5dd; padding: 5px 6px; font-size: 9px;
       letter-spacing: .04em; text-transform: uppercase; text-align: left; }
  td { border: 1px solid #d0d5dd; padding: 5px 6px; font-size: 10px; vertical-align: top; }
  .r { text-align: right; }
  .c { text-align: center; }
  .mono { font-family: "Courier New", monospace; }
  .med { font-weight: 700; }
  .sub { font-family: "Courier New", monospace; font-size: 9px; color: #667085; margin-top: 1px; }
  .foot { display: flex; justify-content: space-between; gap: 16px; margin-top: 12px; }
  .words { flex: 1; font-size: 10px; line-height: 1.5; }
  .words .cap { font-size: 9px; letter-spacing: .08em; text-transform: uppercase; color: #667085; }
  .tot { width: 250px; }
  .tot div { display: flex; justify-content: space-between; padding: 3px 0; font-size: 11px; }
  .tot .net { border-top: 1px solid #101828; margin-top: 4px; padding-top: 5px;
              font-weight: 700; font-size: 12px; }
  .sign { margin-top: 26px; display: flex; justify-content: flex-end; }
  .sign div { width: 210px; border-top: 1px solid #667085; padding-top: 4px;
              text-align: center; font-size: 10px; color: #344054; }
  .note { margin-top: 14px; border-top: 1px dashed #d0d5dd; padding-top: 6px;
          font-size: 9px; color: #667085; line-height: 1.5; }
  .cancelled { color: #b42318; font-weight: 700; }
`;

/* Build the debit-note HTML from the UI note shape (fromPurchaseReturnRow). */
export function buildDebitNote(note = {}) {
  const ph = resolvePharmacy();
  const items = Array.isArray(note.items) ? note.items : [];
  const noteNumber = note.noteNumber || (note.noteId != null ? `#${note.noteId}` : "");
  const status = String(note.status || "DRAFT").toUpperCase();

  const rows = items
    .map((it, i) => {
      const qty = num(it.quantity);
      const rate = num(it.purchasePrice);
      /* Prefer the server's per-line figures; fall back to qty x rate so a
         partial payload still prints something truthful. */
      const taxable = it.taxableAmount != null ? num(it.taxableAmount) : qty * rate;
      const gstPct = it.gstPercentage != null ? num(it.gstPercentage) : null;
      const lineTotal = it.lineTotal != null ? num(it.lineTotal) : taxable;
      return `<tr>
        <td class="c">${i + 1}</td>
        <td>
          <div class="med">${esc(it.medicineName || it.medicineCode || "—")}</div>
          <div class="sub">${esc(it.medicineCode || "")}</div>
        </td>
        <td class="mono">${esc(it.batchNo || "—")}</td>
        <td class="mono c">${esc(fmtExpiry(it.expiryDate))}</td>
        <td class="mono r">${qty.toLocaleString("en-IN")}</td>
        <td class="mono r">${money(rate)}</td>
        <td class="mono r">${money(taxable)}</td>
        <td class="mono c">${gstPct == null ? "—" : `${gstPct}%`}</td>
        <td class="mono r">${money(lineTotal)}</td>
      </tr>`;
    })
    .join("");

  const cgst = num(note.cgstAmount);
  const sgst = num(note.sgstAmount);

  const html = `
  <div class="hdr">
    <div>
      <div class="biz-name">${esc(ph.name)}</div>
      <div class="biz-sub">${esc(ph.sub)}</div>
      <div class="biz-line">${esc(ph.address)}</div>
      <div class="biz-line">GSTIN: <b>${esc(ph.gstin)}</b> &nbsp;·&nbsp; PAN: ${esc(ph.pan)}</div>
      <div class="biz-line">D.L. No: ${esc(ph.dl)}</div>
    </div>
    <div>
      <div class="doc-title">DEBIT NOTE</div>
      <div class="doc-meta">
        No: <b>${esc(noteNumber)}</b><br/>
        Date: <b>${esc(fmtDate(note.noteDate))}</b><br/>
        Against invoice: <b>${esc(note.linkedDocNumber || "—")}</b><br/>
        <span class="status">${esc(status)}</span>
      </div>
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <h4>Debit to (Supplier)</h4>
      <div class="nm">${esc(note.supplierName || note.supplierId || "—")}</div>
      ${note.supplierId ? `<div class="ln">Supplier code: ${esc(note.supplierId)}</div>` : ""}
      <div class="ln">Against purchase invoice: ${esc(note.linkedDocNumber || "—")}</div>
    </div>
    <div class="party">
      <h4>Returned from</h4>
      <div class="nm">${esc(note.warehouseName || note.warehouseId || "—")}</div>
      <div class="ln">Reason: ${esc(String(note.reason || "—").replace(/_/g, " "))}</div>
      <div class="ln">Remarks: ${esc(note.remarks || "—")}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="c" style="width:26px">#</th>
        <th>Medicine</th>
        <th style="width:80px">Batch</th>
        <th class="c" style="width:58px">Expiry</th>
        <th class="r" style="width:40px">Qty</th>
        <th class="r" style="width:62px">Rate</th>
        <th class="r" style="width:70px">Taxable</th>
        <th class="c" style="width:42px">GST</th>
        <th class="r" style="width:74px">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${rows || `<tr><td colspan="9" class="c">No line items</td></tr>`}
    </tbody>
  </table>

  <div class="foot">
    <div class="words">
      <div class="cap">Amount in words</div>
      <div><b>${esc(amountInWords(note.netAmount))}</b></div>
      ${
        status === "CANCELLED"
          ? `<div class="cancelled" style="margin-top:8px">This debit note has been cancelled and carries no accounting effect.</div>`
          : status === "DRAFT"
          ? `<div style="margin-top:8px;color:#b54708">DRAFT — not yet posted. Stock and the supplier ledger are unchanged.</div>`
          : ""
      }
    </div>
    <div class="tot">
      <div><span>Taxable value</span><span class="mono">₹ ${money(note.taxableAmount)}</span></div>
      <div><span>CGST</span><span class="mono">₹ ${money(cgst)}</span></div>
      <div><span>SGST</span><span class="mono">₹ ${money(sgst)}</span></div>
      <div class="net"><span>Total debited</span><span class="mono">₹ ${money(note.netAmount)}</span></div>
    </div>
  </div>

  <div class="sign"><div>Authorised signatory</div></div>

  <div class="note">
    Debit note raised against the purchase invoice shown above. Goods returned to the
    supplier; the supplier's payable stands reduced by the total debited.
    This is a computer-generated document.
  </div>`;

  return { html, styles: STYLES, noteNumber };
}

/* File name used for both the download and the upload copy. */
const fileNameFor = (noteNumber) => `Debit Note ${noteNumber || ""}`.trim() + ".pdf";

/* Render and save the debit note as a PDF. */
export async function downloadDebitNotePdf(note) {
  const { html, styles, noteNumber } = buildDebitNote(note);
  const file = await htmlToPdfBlob({ html, styles, filename: fileNameFor(noteNumber) });
  saveAs(file, fileNameFor(noteNumber));
  return file;
}

/* Open the OS print dialog with the same document. */
export function printDebitNote(note) {
  const { html, styles, noteNumber } = buildDebitNote(note);
  openPrintWindow({ html, styles, billNo: noteNumber });
}
